'use client';

/**
 * The app bar's ⋯ menu (wireframes v3, sections A, B and E).
 *
 * ONE MENU, TWO PRESENTATIONS. On a phone (`variant: 'phone'`) it is the
 * platform's bottom sheet: the same `BottomSheetShell` chrome as "Why this
 * ad", the cookie banner and the tool-approval prompt, with a title row and
 * 44 px rows, because every target on a phone is at least 44 px (RV-024's
 * 20 px dots were the complaint). On a desktop (`variant: 'desktop'`) it is a
 * dropdown under the ⋯ button, the shape the window menu already has, so
 * lane B's desktop full screen and the app's own address get the same rows
 * in the shape their pointer expects.
 *
 * ROWS ARE DATA. A row is an action `{ id, label, icon, onSelect }` or a
 * switch `{ kind: 'switch', id, label, checked, onChange }` (the app's own
 * address needs "Show menu"). A disabled action stays readable and keeps the
 * menu open, the window menu's convention: closing on a dead row made the
 * click look like a silently failed action. A switch keeps the menu open too,
 * so the state change is seen where it happened. An action closes the menu
 * first and then runs, so a row that opens a dialog never finds the sheet
 * still over it.
 *
 * TWO MORE KINDS FOR THE APP'S OWN ADDRESS (lane B, 2026-09-29). A LINK row
 * `{ kind: 'link', id, label, icon, href, onSelect? }` is a real `<a>`, so a
 * middle click, a cmd-click and the status bar all work: on
 * revent.robutler.app, Open on my Stage and the Discover, Stage and You rows
 * go to robutler.ai, because the host's viewer-tier session cannot draw them
 * in place. `onSelect` runs before the navigation (the sign-in row parks the
 * arrival fragment there), and the menu closes one tick later, so the anchor
 * is still in the document when the browser follows it. A HEADING row
 * `{ kind: 'heading', id, label }` is a group label and not a control: the
 * desktop dropdown's "On robutler.ai" over those three links. It never takes
 * the opening focus; the first row that can does. Both kinds are additive:
 * a menu built from actions and switches renders exactly as before.
 *
 * FOCUS. The first row takes focus on open; Esc, the backdrop (phone) or a
 * click outside (desktop) close; focus returns to the ⋯ button. Rows are
 * `menuitem` / `menuitemcheckbox` inside a `menu`, and the phone sheet is a
 * modal dialog named after the app.
 *
 * A PRESS INSIDE THE APP CLOSES THE DROPDOWN TOO (2026-10-02). The app under
 * the bar is a frame, and a frame's own document gets its pointer and key
 * events: this page never sees the press, and once focus is inside the frame
 * it never sees Esc either. The visual check of the `/w` bar found exactly
 * that, on `/w` and in full screen alike: the dropdown stayed open over the
 * app and nothing closed it. The one signal this page does get is its window
 * losing focus to the frame, so that closes the dropdown. Focus is NOT pulled
 * back to ⋯ then: the person has just chosen the app, and taking the focus
 * away from it would swallow their next key. The phone sheet has a backdrop
 * over the whole page, so no press can reach a frame while it is open.
 */

import { Fragment, useCallback, useEffect, useRef, type ComponentType, type RefObject } from 'react';
import { useT } from '@/components/i18n-provider';
import { BottomSheetShell } from '@/components/ui/bottom-sheet-shell';
import { PopoverPortal } from '@/components/ui/popover-portal';
import { cn } from '@/lib/utils';

export type AppBarMenuIcon = ComponentType<{ className?: string }>;

export interface AppBarMenuAction {
  kind?: 'action';
  id: string;
  label: string;
  icon?: AppBarMenuIcon;
  onSelect: () => void;
  /** Dimmed and inert. Put the reason in the label; nothing said after a click is seen. */
  disabled?: boolean;
  /** A lit dot at the row's end: the state this row toggles is on. */
  active?: boolean;
  /**
   * A count chip at the row's end (the Notifications row of a full-screen
   * bar, 2026-10-03): `text` is what the chip shows, `label` what is read
   * out for it after the row's own label ("Notifications, 3 unread"; the
   * chip itself is drawn, not read). It wears the red of the chip on ⋯,
   * 40 px above it and on screen at the same moment: it was the Account
   * panel's rose at first, and the visual pass of 2026-10-03 saw two reds
   * in one glance.
   */
  badge?: { text: string; label: string };
  /** A hairline above this row: it starts a new group (ignored on the first row). */
  separatorBefore?: boolean;
}

export interface AppBarMenuSwitch {
  kind: 'switch';
  id: string;
  label: string;
  icon?: AppBarMenuIcon;
  checked: boolean;
  onChange: (next: boolean) => void;
}

/** A navigation row, drawn as a real link (see the file note). */
export interface AppBarMenuLink {
  kind: 'link';
  id: string;
  label: string;
  icon?: AppBarMenuIcon;
  href: string;
  /** Runs on the click, before the browser follows the link. */
  onSelect?: () => void;
  /** A hairline above this row: it starts a new group (ignored on the first row). */
  separatorBefore?: boolean;
}

/** A group label over the rows after it; not a control, never focused. */
export interface AppBarMenuHeading {
  kind: 'heading';
  id: string;
  label: string;
}

export type AppBarMenuRow = AppBarMenuAction | AppBarMenuSwitch | AppBarMenuLink | AppBarMenuHeading;

export function isSwitchRow(row: AppBarMenuRow): row is AppBarMenuSwitch {
  return row.kind === 'switch';
}

export function isLinkRow(row: AppBarMenuRow): row is AppBarMenuLink {
  return row.kind === 'link';
}

export function isHeadingRow(row: AppBarMenuRow): row is AppBarMenuHeading {
  return row.kind === 'heading';
}

export interface AppBarMenuProps {
  variant: 'phone' | 'desktop';
  open: boolean;
  /** The app's name: the sheet's title row and the dialog's accessible name. */
  title: string;
  rows: AppBarMenuRow[];
  onClose: () => void;
  /** The ⋯ button, which takes focus back when the menu closes. */
  returnFocusTo: RefObject<HTMLElement | null>;
}

export function AppBarMenu({ variant, open, title, rows, onClose, returnFocusTo }: AppBarMenuProps) {
  const t = useT();
  const panelRef = useRef<HTMLDivElement>(null);
  // Focus the first row the moment it ATTACHES. A ref read from an effect
  // would miss it: the phone sheet mounts through `PopoverPortal`, which
  // renders its children one effect later than this component's own effects
  // run, so on the opening render the ref is still null.
  const focusFirst = useCallback((el: HTMLElement | null) => {
    el?.focus();
  }, []);

  // Esc closes (capture phase, so an app iframe's own Esc handling is not
  // needed and a canvas Esc consumer does not swallow it first); an outside
  // press closes on the desktop dropdown. Focus returns to ⋯ on close.
  useEffect(() => {
    if (!open) return;
    let live = true;
    // Closed because the person pressed inside the app's frame: the focus is
    // theirs to keep (see the file note), so the cleanup leaves it alone.
    let wentToFrame = false;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      onClose();
    };
    const onPress = (e: PointerEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      if (panelRef.current?.contains(target) || returnFocusTo.current?.contains(target)) return;
      onClose();
    };
    // The window loses focus to a frame when a press lands inside it. The
    // active element is read one tick later: some engines move it after the
    // blur event, not before.
    const onWindowBlur = () => {
      window.setTimeout(() => {
        if (!live) return;
        if (!(document.activeElement instanceof HTMLIFrameElement)) return;
        wentToFrame = true;
        onClose();
      }, 0);
    };
    document.addEventListener('keydown', onKey, true);
    if (variant === 'desktop') {
      document.addEventListener('pointerdown', onPress, true);
      window.addEventListener('blur', onWindowBlur);
    }
    const anchor = returnFocusTo.current;
    return () => {
      live = false;
      document.removeEventListener('keydown', onKey, true);
      document.removeEventListener('pointerdown', onPress, true);
      window.removeEventListener('blur', onWindowBlur);
      if (!wentToFrame) anchor?.focus();
    };
  }, [open, variant, onClose, returnFocusTo]);

  if (!open) return null;

  const phone = variant === 'phone';
  const rowClass = cn(
    'flex w-full items-center gap-3 rounded-lg text-left transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
    phone ? 'h-11 px-3 text-[15px]' : 'h-9 px-3 text-[13px]',
  );

  // The opening focus goes to the first row that can take it: a heading is a
  // label, never a stop. Without headings this is row 0, as it always was.
  const firstFocusable = rows.findIndex((row) => !isHeadingRow(row));

  const list = (
    <div role="menu" aria-label={title} className={cn(phone ? 'space-y-0.5' : 'py-1')}>
      {rows.map((row, i) => {
        if (isHeadingRow(row)) {
          return (
            <div
              key={row.id}
              role="presentation"
              data-row-id={row.id}
              data-row-kind="heading"
              className={cn(
                'select-none px-3 pb-1 font-medium text-muted-foreground',
                phone ? 'pt-2 text-[12px]' : 'pt-1.5 text-[11px]',
                i > 0 && 'mt-1 border-t border-border/60',
              )}
            >
              {row.label}
            </div>
          );
        }
        const Icon = row.icon;
        const iconEl = Icon ? <Icon className={cn('shrink-0 text-muted-foreground', phone ? 'h-5 w-5' : 'h-4 w-4')} /> : null;
        if (isLinkRow(row)) {
          return (
            <Fragment key={row.id}>
              {row.separatorBefore && i > 0 ? (
                <div role="separator" data-row-separator className={cn('border-t border-border/60', phone ? 'my-1.5' : 'my-1')} />
              ) : null}
            <a
              ref={i === firstFocusable ? focusFirst : undefined}
              href={row.href}
              role="menuitem"
              data-row-id={row.id}
              data-row-kind="link"
              onClick={(e) => {
                e.stopPropagation();
                row.onSelect?.();
                // One tick later: the anchor must still be in the document
                // while the browser runs the navigation the click started.
                window.setTimeout(onClose, 0);
              }}
              className={cn(rowClass, 'text-foreground/85 hover:bg-foreground/8')}
            >
              {iconEl}
              <span className="min-w-0 flex-1 truncate">{row.label}</span>
            </a>
            </Fragment>
          );
        }
        if (isSwitchRow(row)) {
          return (
            <button
              key={row.id}
              ref={i === firstFocusable ? focusFirst : undefined}
              type="button"
              role="menuitemcheckbox"
              aria-checked={row.checked}
              data-row-id={row.id}
              onClick={(e) => {
                e.stopPropagation();
                row.onChange(!row.checked);
              }}
              className={cn(rowClass, 'text-foreground hover:bg-foreground/8')}
            >
              {iconEl}
              <span className="min-w-0 flex-1 truncate">{row.label}</span>
              <span
                aria-hidden
                className={cn(
                  'ml-auto inline-flex h-5 w-9 shrink-0 items-center rounded-full p-0.5 transition-colors',
                  row.checked ? 'bg-primary' : 'bg-foreground/20',
                )}
              >
                <span
                  className={cn(
                    'h-4 w-4 rounded-full bg-background shadow transition-transform',
                    row.checked && 'translate-x-4',
                  )}
                />
              </span>
            </button>
          );
        }
        return (
          <Fragment key={row.id}>
            {row.separatorBefore && i > 0 ? (
              <div role="separator" data-row-separator className={cn('border-t border-border/60', phone ? 'my-1.5' : 'my-1')} />
            ) : null}
          <button
            ref={i === firstFocusable ? focusFirst : undefined}
            type="button"
            role="menuitem"
            aria-disabled={row.disabled || undefined}
            // A row with a count names itself whole ("Notifications, 3
            // unread"): the chip is drawn, and two inline pieces would be
            // read run together, "Notifications3".
            aria-label={row.badge ? `${row.label}, ${row.badge.label}` : undefined}
            data-row-id={row.id}
            onClick={(e) => {
              e.stopPropagation();
              if (row.disabled) return;
              onClose();
              row.onSelect();
            }}
            className={cn(
              rowClass,
              row.disabled
                ? 'cursor-default text-foreground/45'
                : cn('hover:bg-foreground/8', row.active ? 'font-medium text-foreground' : 'text-foreground/85'),
            )}
          >
            {iconEl}
            <span className="min-w-0 flex-1 truncate">{row.label}</span>
            {row.badge && (
              <span
                aria-hidden
                data-row-badge
                className="ml-auto inline-flex h-4 min-w-4 shrink-0 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-semibold leading-none text-destructive-foreground"
              >
                {row.badge.text}
              </span>
            )}
            {row.active && <span aria-hidden className="ml-auto h-1.5 w-1.5 shrink-0 rounded-full bg-sky-500" />}
          </button>
          </Fragment>
        );
      })}
    </div>
  );

  if (phone) {
    return (
      <PopoverPortal role="presentation" className="fixed inset-0 z-[300]">
        <div aria-hidden className="absolute inset-0 bg-black/30" onClick={onClose} />
        <div
          ref={panelRef}
          className="absolute inset-x-0 bottom-0 nodrag nopan"
          role="dialog"
          aria-modal="true"
          aria-label={title}
          data-testid="app-bar-sheet"
          onClick={(e) => e.stopPropagation()}
        >
          <BottomSheetShell radiusMode="docked" showHandle safeAreaBottom contentClassName="px-2 pb-2 pt-1">
            <div className="flex h-11 items-center px-3">
              <span className="min-w-0 flex-1 truncate text-[15px] font-semibold text-foreground">{title}</span>
              <button
                type="button"
                onClick={onClose}
                className="ml-2 h-11 shrink-0 rounded-lg px-3 text-[13px] text-muted-foreground hover:bg-foreground/8 hover:text-foreground"
              >
                {t('actions.dismiss')}
              </button>
            </div>
            {list}
          </BottomSheetShell>
        </div>
      </PopoverPortal>
    );
  }

  return (
    <div
      ref={panelRef}
      // 8 px in from the bar's end: flush right the panel touched the
      // viewport's edge under a full-screen bar (the design pass, 2026-09-30).
      className="glass-panel nodrag nopan absolute right-2 top-full z-50 mt-1 w-60 rounded-lg shadow-2xl"
      data-testid="app-bar-dropdown"
      onClick={(e) => e.stopPropagation()}
    >
      {list}
    </div>
  );
}
